import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, Flame, ListChecks, Brain, PenSquare, Clapperboard, Eye, MessageCircle, Heart, Vote, UserPlus, Smartphone, BellRing, Cake,
  Disc3, Ticket, Swords, Map, Dices, CloudRain, Gift, PawPrint, Shirt, Users, Target, Trophy, Zap, Star, Sparkles, ArrowRight, ChevronRight,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { SectionHead } from "@/components/landing/Kit";

const GROUPS = [
  ["Jeden Tag", [
    [CalendarCheck, "Täglicher Login", "Melde dich einmal am Tag an – schon bekommst du deinen Login-Bonus gutgeschrieben."],
    [Flame, "Login-Serie", "Komm mehrere Tage hintereinander vorbei. Je länger deine Serie, desto größer der Extra-Bonus."],
    [ListChecks, "Tagesmissionen", "Jeden Tag warten kleine Aufgaben auf dich, z. B. liken oder kommentieren. Alle erledigt? Dann holst du dir den Bonus ab."],
    [Brain, "Tagesquiz", "Beantworte täglich ein paar Quizfragen – für jede richtige Antwort gibt es Punkte."],
  ]],
  ["Aktiv sein", [
    [PenSquare, "Beitrag posten", "Teile Fotos, Gedanken oder Umfragen im Feed – jeder neue Beitrag bringt dir Punkte."],
    [Clapperboard, "Story posten", "Zeig deinen Tag in einer Story, die 24 Stunden sichtbar bleibt – dafür gibt es Punkte."],
    [Eye, "Storys ansehen", "Schau dir die Storys der Community an und sammle nebenbei Punkte."],
    [MessageCircle, "Kommentieren", "Schreib unter Beiträge, was du denkst – Kommentare werden mit Punkten belohnt."],
    [Heart, "Liken", "Dir gefällt ein Beitrag? Ein Herz verteilen bringt dir ebenfalls Punkte."],
    [Vote, "Bei Umfragen abstimmen", "Stimm bei Umfragen im Feed ab und sag deine Meinung – das gibt Punkte."],
  ]],
  ["Spiele", [
    [Disc3, "Glücksrad", "Dreh am Glücksrad und gewinne Punkte – mit etwas Glück sogar den Jackpot."],
    [Ticket, "Rubbellose", "Rubbel dein Los frei und schau, ob ein Punkte-Gewinn darunter steckt."],
    [Swords, "Duelle", "Fordere andere Mitglieder zum Duell heraus. Wer gewinnt, bekommt die Punkte."],
    [Map, "Schatzsuche", "Such auf der Schatzkarte nach versteckten Schätzen und sichere dir Punkte."],
    [Dices, "Punkte-Lotto", "Tippe jede Woche 6 aus 49. Je mehr Richtige, desto mehr Punkte – bis zum Jackpot."],
    [CloudRain, "Goldregen", "Ab und zu regnet es Gold in der App – sei schnell und sammle die Münzen ein."],
    [Gift, "Mystery-Box", "Öffne die Mystery-Box und lass dich überraschen, was drin steckt."],
    [PawPrint, "Haustier", "Kümmere dich um dein eigenes Haustier, füttere es und lass es wachsen – das wird belohnt."],
    [Shirt, "Haustier-Spielplatz", "Style dein Haustier und sammle Herzen für das schönste Outfit."],
  ]],
  ["Gemeinsam", [
    [Target, "Community-Aufgaben", "Die ganze Community arbeitet zusammen an einem Ziel. Schafft ihr es, gibt es Punkte für alle Mitmacher."],
    [Users, "Team-Duell", "Wähle Team Saskia oder Team Stefan und sammle gemeinsam Punkte fürs Wochenziel."],
    [Trophy, "Wochen-Challenge", "Jede Woche gibt es eine neue Challenge – mach mit und hol dir die Belohnung."],
    [Zap, "Spontane Aufgaben", "Überraschungs-Aufgaben, die plötzlich auftauchen. Wer schnell ist, sichert sich Punkte."],
    [Star, "Sternenhimmel", "Gestalte gemeinsam mit allen den Sternenhimmel und werde dafür belohnt."],
  ]],
  ["Einmalig & Extras", [
    [UserPlus, "Freunde einladen", "Teile deinen Einladungslink. Meldet sich ein Freund an, bekommt ihr beide Punkte."],
    [Smartphone, "App installieren", "Leg dir itzPlay als App auf den Home-Bildschirm – dafür gibt es einmalig Punkte."],
    [BellRing, "Push aktivieren", "Erlaube Push-Mitteilungen, damit du nichts verpasst – einmalig mit Punkten belohnt."],
    [Cake, "Geburtstags-Geschenk", "Trag deinen Geburtstag im Profil ein und freu dich an deinem Ehrentag über ein Punkte-Geschenk."],
  ]],
];

const Tile = ({ icon: Icon, label, i, onOpen }) => (
  <button type="button" onClick={onOpen} data-testid={`earn-tile-${label}`}
    className="earn-glow group w-full text-left flex items-center gap-2.5 sm:gap-3 rounded-2xl px-3 sm:px-4 py-3 sm:py-3.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300">
    <span className="earn-glow-icon w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-xl pink-gradient text-white flex items-center justify-center"><Icon className="w-[18px] h-[18px] sm:w-5 sm:h-5" /></span>
    <span className="flex-1 text-[13px] sm:text-sm font-bold text-slate-800 leading-tight break-words min-w-0">{label}</span>
    <ChevronRight className="hidden sm:block w-4 h-4 shrink-0 text-rose-400" />
  </button>
);

const Explain = ({ item, onClose }) => {
  const [Icon, label, text] = item || [Sparkles, "", ""];
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm rounded-3xl border-pink-100 p-6 text-center" data-testid="earn-explain-dialog">
        <span className="mx-auto w-14 h-14 rounded-2xl pink-gradient text-white flex items-center justify-center shadow-lg shadow-rose-200"><Icon className="w-7 h-7" /></span>
        <DialogTitle className="font-display text-xl font-extrabold" data-testid="earn-explain-title">{label}</DialogTitle>
        <DialogDescription className="text-sm text-slate-600 leading-relaxed" data-testid="earn-explain-text">{text}</DialogDescription>
        <Link to="/registrieren" data-testid="earn-explain-cta" className="mt-2 inline-flex items-center justify-center gap-2 h-11 rounded-full pink-gradient text-white font-bold text-sm shadow-lg shadow-rose-200">Kostenlos mitmachen <ArrowRight className="w-4 h-4" /></Link>
        <button onClick={onClose} data-testid="earn-explain-close" className="text-sm font-semibold text-slate-500 hover:text-rose-600">Schließen</button>
      </DialogContent>
    </Dialog>
  );
};

export const EarnTiles = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-8" data-testid="landing-earn">
      <SectionHead eyebrow="Punkte sammeln" icon={Sparkles} title="So sammelst du" accent="Punkte" lead="Fast alles, was du in der Community machst, bringt dir Punkte – und die tauschst du gegen tolle Geschenke. Tippe auf eine Kachel für mehr Infos." />
      <div className="space-y-7">
        {GROUPS.map(([group, items]) => (
          <div key={group} className="space-y-3" data-testid={`landing-earn-${group}`}>
            <h3 className="text-xs font-extrabold uppercase tracking-[0.2em] text-rose-500">{group}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {items.map((it, i) => <Tile key={it[1]} icon={it[0]} label={it[1]} i={i} onOpen={() => setOpen(it)} />)}
            </div>
          </div>
        ))}
      </div>
      <Link to="/registrieren" data-testid="landing-earn-cta" className="inline-flex items-center gap-2 px-6 h-12 rounded-full pink-gradient text-white font-bold text-sm shadow-lg shadow-rose-200 hover:scale-[1.02] transition-transform">Jetzt losstarten <ArrowRight className="w-4 h-4" /></Link>
      <Explain item={open} onClose={() => setOpen(null)} />
    </section>
  );
};
